import * as React from "react";
import {
  Download,
  Eye,
  Play,
  RefreshCw,
  RotateCcw,
  Square,
  Trash2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { StatusBadge } from "./status-badge";
import { TypeBadge } from "./type-badge";
import type { KnowledgeDocument } from "../../domain/entities/document";

function RowActionTooltip({
  label,
  children,
}: {
  label: string;
  children: React.ReactElement<{ disabled?: boolean }>;
}) {
  const disabled = Boolean(children.props.disabled);
  const trigger = disabled ? (
    <span className="inline-flex">{children}</span>
  ) : (
    children
  );
  return (
    <Tooltip>
      <TooltipTrigger asChild>{trigger}</TooltipTrigger>
      <TooltipContent side="top">{label}</TooltipContent>
    </Tooltip>
  );
}

export type DocumentRowBusy = {
  kind: "parse" | "reparse" | "cancel" | "retry" | "download";
  docId: string;
} | null;

const isPipelineKind = (b: DocumentRowBusy): boolean =>
  b !== null && b.kind !== "download";

type Props = {
  document: KnowledgeDocument;
  onView: (doc: KnowledgeDocument) => void;
  onDownload: (doc: KnowledgeDocument) => void;
  onParse: (id: string) => void;
  onReparse: (id: string) => void;
  onCancel: (id: string) => void;
  onRetry: (id: string) => void;
  onDelete: (doc: KnowledgeDocument) => void;
  busy: DocumentRowBusy;
};

export const DocumentRow = ({
  document: doc,
  onView,
  onDownload,
  onParse,
  onReparse,
  onCancel,
  onRetry,
  onDelete,
  busy,
}: Props) => {
  const is = (kind: NonNullable<DocumentRowBusy>["kind"]) =>
    busy?.kind === kind && busy.docId === doc.id;

  const isParseBusy = is("parse");
  const isReparseBusy = is("reparse");
  const isCancelBusy = is("cancel");
  const isRetryBusy = is("retry");
  const isDownloadBusy = is("download");

  const otherDocPipelineBusy =
    busy && isPipelineKind(busy) && busy.docId !== doc.id;

  // A document is "being analyzed" (and thus cancellable) while the pipeline runs,
  // which spans both the main processing stages and the final metadata-extraction
  // stage. Keep this in sync with the backend cancel guard.
  const isAnalyzing =
    doc.status === "processing" || doc.status === "extracting_metadata";

  return (
    <div className="flex flex-col gap-3 px-4 py-3 transition-colors hover:bg-secondary/50 sm:flex-row sm:items-center sm:gap-4">
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium">{doc.name}</p>
      </div>
      <div className="flex shrink-0 flex-wrap items-center gap-2 sm:justify-end">
        <StatusBadge status={doc.status} />
        <TypeBadge type={doc.documentType} />
      </div>
      <div className="flex shrink-0 flex-wrap items-center justify-end gap-1">
        {isAnalyzing ? (
          <RowActionTooltip label="Cancel analyzing">
            <Button
              variant="ghost"
              size="icon"
              className="size-8 cursor-pointer text-muted-foreground hover:text-foreground"
              onClick={(e) => {
                e.stopPropagation();
                onCancel(doc.id);
              }}
              disabled={!!otherDocPipelineBusy || isCancelBusy}
              aria-label={`Cancel analyzing for ${doc.name}`}
            >
              <Square className="size-4" />
            </Button>
          </RowActionTooltip>
        ) : (
          <>
            <RowActionTooltip label="View">
              <Button
                variant="ghost"
                size="icon"
                className="size-8 cursor-pointer text-muted-foreground hover:text-foreground"
                onClick={(e) => {
                  e.stopPropagation();
                  onView(doc);
                }}
                aria-label={`View ${doc.name}`}
              >
                <Eye className="size-4" />
              </Button>
            </RowActionTooltip>

            <RowActionTooltip label="Download">
              <Button
                variant="ghost"
                size="icon"
                className="size-8 cursor-pointer text-muted-foreground hover:text-foreground"
                onClick={(e) => {
                  e.stopPropagation();
                  onDownload(doc);
                }}
                disabled={isDownloadBusy}
                aria-label={`Download ${doc.name}`}
              >
                <Download className="size-4" />
              </Button>
            </RowActionTooltip>

            {doc.status === "uploaded" && (
              <RowActionTooltip label="Analyze">
                <Button
                  variant="ghost"
                  size="icon"
                  className="size-8 cursor-pointer text-muted-foreground hover:text-foreground"
                  onClick={(e) => {
                    e.stopPropagation();
                    onParse(doc.id);
                  }}
                  disabled={!!otherDocPipelineBusy || isParseBusy}
                  aria-label={`Analyze ${doc.name}`}
                >
                  <Play className="size-4" />
                </Button>
              </RowActionTooltip>
            )}

            {doc.status === "failed" && (
              <RowActionTooltip label="Resume after error">
                <Button
                  variant="ghost"
                  size="icon"
                  className="size-8 cursor-pointer text-muted-foreground hover:text-foreground"
                  onClick={(e) => {
                    e.stopPropagation();
                    onRetry(doc.id);
                  }}
                  disabled={!!otherDocPipelineBusy || isRetryBusy}
                  aria-label={`Resume analyzing after error for ${doc.name}`}
                >
                  <RotateCcw className="size-4" />
                </Button>
              </RowActionTooltip>
            )}

            {(doc.status === "completed" || doc.status === "failed") && (
              <RowActionTooltip
                label={
                  doc.status === "failed"
                    ? "New analyze from file"
                    : "Analyze again"
                }
              >
                <Button
                  variant="ghost"
                  size="icon"
                  className="size-8 cursor-pointer text-muted-foreground hover:text-foreground"
                  onClick={(e) => {
                    e.stopPropagation();
                    onReparse(doc.id);
                  }}
                  disabled={!!otherDocPipelineBusy || isReparseBusy}
                  aria-label={
                    doc.status === "failed"
                      ? `Start a new analyze from file for ${doc.name}`
                      : `Analyze ${doc.name} again`
                  }
                >
                  <RefreshCw className="size-4" />
                </Button>
              </RowActionTooltip>
            )}

            <RowActionTooltip label="Delete">
              <Button
                variant="ghost"
                size="icon"
                className="size-8 cursor-pointer text-muted-foreground hover:text-destructive"
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete(doc);
                }}
                aria-label={`Delete ${doc.name}`}
              >
                <Trash2 className="size-4" />
              </Button>
            </RowActionTooltip>
          </>
        )}
      </div>
    </div>
  );
};
